import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../apiService';
import { toast } from 'react-toastify';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

// --- Internal Component: ClientForm ---
function ClientForm({ onBack, clientToEdit }) {
    const [isCheckingCnic, setIsCheckingCnic] = useState(false);
    const [cnicStatus, setCnicStatus] = useState({ available: true, message: '' });
    const [isCheckingContact, setIsCheckingContact] = useState(false);
    const [contactStatus, setContactStatus] = useState({ available: true, message: '' });
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [emailStatus, setEmailStatus] = useState({ available: true, message: '' });

    const cnicCheckTimer = useRef(null);
    const contactCheckTimer = useRef(null);
    const emailCheckTimer = useRef(null);

    const [formData, setFormData] = useState({ 
        name: '', 
        companyName: '', 
        contact: '', 
        email: '', 
        companyAddress: '', 
        cnic: '',
        ntn: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    useEffect(() => { 
        if (clientToEdit) { 
            setFormData({
                name: clientToEdit.name || '',
                companyName: clientToEdit.companyName || '',
                contact: clientToEdit.contact || '',
                email: clientToEdit.email || '',
                companyAddress: clientToEdit.companyAddress || '',
                cnic: clientToEdit.cnic || '',
                ntn: clientToEdit.ntn || '',
                id: clientToEdit.id
            });
        } else {
            setFormData({ name: '', companyName: '', contact: '', email: '', companyAddress: '', cnic: '', ntn: '' });
        }
    }, [clientToEdit]);
    
    const checkDuplicate = useCallback(async (field, value, originalId = null) => {
        if (!value) return { available: true, message: '' };

        try {
            const results = await api.get('magazine_clients', { where: { [field]: value } });
            
            if (results.length === 0) {
                return { available: true, message: `✅ This ${field} is available.` };
            } else {
                const docData = results[0];
                if (originalId && String(docData.id) === String(originalId)) {
                     return { available: true, message: '' };
                }
                return { available: false, message: `⚠️ This ${field} is already registered with ${docData.name}.` };
            }
        } catch (err) {
            console.error(err);
            return { available: true, message: '' };
        }
    }, []);

    const handleChange = (e) => { 
        const { id, value } = e.target;
        let formattedValue = value;

        if (id === 'cnic') {
            const cnic = value.replace(/\D/g, '');
            formattedValue = cnic.length > 5 ? `${cnic.slice(0, 5)}-${cnic.slice(5, 12)}` : cnic;
            if (cnic.length > 12) formattedValue += `-${cnic.slice(12, 13)}`;
            
            clearTimeout(cnicCheckTimer.current);
            if (formattedValue.length === 15) {
                setIsCheckingCnic(true);
                cnicCheckTimer.current = setTimeout(async () => {
                   const status = await checkDuplicate('cnic', formattedValue, clientToEdit?.id);
                   setCnicStatus(status);
                   setIsCheckingCnic(false);
                }, 500);
            } else {
               setCnicStatus({ available: true, message: '' });
            }
        }

        if (id === 'contact') {
            const numbers = value.replace(/\D/g, '');
            if (numbers.startsWith('0')) {
                formattedValue = '92' + numbers.substring(1);
            } else if (numbers.startsWith('92')) {
                formattedValue = numbers;
            } else if (numbers.length > 0) {
                formattedValue = '92' + numbers;
            } else {
                formattedValue = numbers;
            }
            formattedValue = formattedValue.slice(0, 12); 

            clearTimeout(contactCheckTimer.current);
            if(formattedValue.length === 12){
                setIsCheckingContact(true);
                contactCheckTimer.current = setTimeout(async () => {
                    const status = await checkDuplicate('contact', formattedValue, clientToEdit?.id);
                    setContactStatus(status);
                    setIsCheckingContact(false);
                }, 500);
            } else {
                 setContactStatus({ available: true, message: '' });
            }
        }
        
        if (id === 'email') {
            formattedValue = value.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            clearTimeout(emailCheckTimer.current);
            if (!formattedValue) {
                setEmailStatus({ available: true, message: '' });
            } else if (emailRegex.test(formattedValue)) {
                setIsCheckingEmail(true);
                emailCheckTimer.current = setTimeout(async () => {
                    const status = await checkDuplicate('email', formattedValue, clientToEdit?.id);
                    setEmailStatus(status);
                    setIsCheckingEmail(false);
                }, 500);
            } else {
                setEmailStatus({ available: false, message: '⚠️ Invalid email format.' });
            }
        }

        setFormData({ ...formData, [id]: formattedValue }); 
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (clientToEdit) {
                await api.put('magazine_clients', clientToEdit.id, formData);
                toast.success('Client updated successfully');
            } else {
                await api.post('magazine_clients', formData);
                toast.success('Client added successfully');
            }
            onBack();
        } catch (error) { toast.error(`Failed to save client: ${error.message}`); } 
        finally { setIsSubmitting(false); }
    };
    
    const CheckingSpinner = () => <div className="absolute inset-y-0 right-0 pr-3 flex items-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rose-500"></div></div>;

    return (
        <div className="container mx-auto">
             <div className="mb-8">
                <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-800">
                    <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" /> Back to Client List
                </button>
                <h1 className="text-4xl font-bold text-gray-800 mt-2">{clientToEdit ? 'Edit Client' : 'Add New Client'}</h1>
            </div>
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-lg space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label htmlFor="name" className="block text-sm font-medium text-gray-700">Client Name <span className="text-red-500">*</span></label><input type="text" id="name" value={formData.name} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., Ali Ahmed" required /></div>
                    <div><label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Company Name <span className="text-red-500">*</span></label><input type="text" id="companyName" value={formData.companyName} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., Ali Traders" required /></div>
                    
                    <div>
                        <label htmlFor="contact" className="block text-sm font-medium text-gray-700">Contact Number (Unique) <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input type="tel" id="contact" value={formData.contact} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., 923001234567" required maxLength="12" />
                            {isCheckingContact && <CheckingSpinner />}
                        </div>
                        {contactStatus.message && <p className={`text-sm mt-1 ${contactStatus.available ? 'text-green-600' : 'text-red-600'}`}>{contactStatus.message}</p>}
                    </div>
                    
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address (Unique) (Optional)</label>
                        <div className="relative">
                            <input type="email" id="email" value={formData.email} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., info@alitraders.com" />
                            {isCheckingEmail && <CheckingSpinner />}
                        </div>
                        {emailStatus.message && <p className={`text-sm mt-1 ${emailStatus.available ? 'text-green-600' : 'text-red-600'}`}>{emailStatus.message}</p>}
                    </div>
                    
                    <div>
                        <label htmlFor="cnic" className="block text-sm font-medium text-gray-700">CNIC (Unique) <span className="text-red-500">*</span></label>
                        <div className="relative">
                           <input type="text" id="cnic" value={formData.cnic} onChange={handleChange} maxLength="15" className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., 35202-1234567-8" required />
                           {isCheckingCnic && <CheckingSpinner />}
                        </div>
                        {cnicStatus.message && <p className={`text-sm mt-1 ${cnicStatus.available ? 'text-green-600' : 'text-red-600'}`}>{cnicStatus.message}</p>}
                    </div>

                    <div><label htmlFor="ntn" className="block text-sm font-medium text-gray-700">NTN (Optional)</label><input type="text" id="ntn" value={formData.ntn} onChange={handleChange} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., 1234567-8" /></div>
                    
                    <div className="md:col-span-2">
                        <label htmlFor="companyAddress" className="block text-sm font-medium text-gray-700">Company Address <span className="text-red-500">*</span></label>
                        <textarea id="companyAddress" value={formData.companyAddress} onChange={handleChange} rows="3" className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="e.g., Shop # 1, Main Market, Lahore" required />
                    </div>
                </div>
                <div className="flex justify-end pt-6 border-t">
                    <button type="submit" disabled={isSubmitting || isCheckingCnic || isCheckingContact || isCheckingEmail || emailStatus.available === false} className="bg-rose-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed">
                        {isSubmitting ? 'Saving...' : (clientToEdit ? 'Save Changes' : 'Add Client')}
                    </button>
                </div>
            </form>
        </div>
    );
}

// --- Main Component: MagazineClientSection ---
function MagazineClientSection() {
    const [view, setView] = useState('list');
    const [editingClient, setEditingClient] = useState(null);
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [pageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalClients, setTotalClients] = useState(0);

    const getInitials = (name) => {
        if (!name) return '?';
        const words = name.split(' ');
        if (words.length > 1 && words[1]) return `${words[0][0]}${words[1][0]}`.toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    const fetchClients = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await api.get('magazine_clients');
            setClients(data);
            setTotalClients(data.length);
        } catch (error) {
            toast.error(`Failed to fetch clients: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);
    
    const filteredClients = clients.filter(c => 
        (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.companyName && c.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    const handleEdit = (client) => { setEditingClient(client); setView('form'); }
    const handleAddNew = () => { setEditingClient(null); setView('form'); }
    
    if (view === 'form') {
        return <ClientForm onBack={() => { setView('list'); fetchClients(); }} clientToEdit={editingClient} />;
    }
    
    const pageStart = totalClients > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const pageEnd = Math.min(currentPage * pageSize, totalClients);
    const currentClients = filteredClients.slice(pageStart - 1, pageEnd);

    return (
        <div className="container mx-auto">
            <div className="mb-8 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <div><h1 className="text-4xl font-bold text-gray-800">Clients</h1><p className="text-gray-500 mt-1">Manage Flare clients.</p></div>
                <button onClick={handleAddNew} className="text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-transform hover:scale-105 flex items-center gap-2" style={{backgroundColor: '#c4151c'}}>
                    <Icon path="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    <span>Add New Client</span>
                </button>
            </div>
            
            <div className="mb-6"><input type="text" placeholder="Search by client or company name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-white border-gray-300 rounded-lg shadow-sm p-4" /></div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left">Client / Company</th>
                                <th className="px-6 py-3 text-left hidden md:table-cell">Contact Info</th>
                                <th className="px-6 py-3 text-left">CNIC / NTN</th>
                                <th className="px-6 py-3 text-left hidden lg:table-cell">Address</th>
                                <th className="px-6 py-3 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {isLoading ? (<tr><td colSpan="5" className="text-center p-8 text-gray-500">Loading...</td></tr>) 
                        : currentClients.length > 0 ? currentClients.map(client => (
                            <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 font-bold">
                                            {getInitials(client.name)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-gray-800">{client.name}</div>
                                            <div className="text-sm text-gray-500">{client.companyName}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 hidden md:table-cell">
                                    <div className="text-sm text-gray-800">{client.contact}</div>
                                    <div className="text-xs text-gray-500">{client.email}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm text-gray-700 font-mono">{client.cnic}</div>
                                    <div className="text-xs text-gray-500">NTN: {client.ntn || 'N/A'}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 hidden lg:table-cell max-w-xs truncate">{client.companyAddress}</td>
                                <td className="px-6 py-4 text-center">
                                    <button onClick={() => handleEdit(client)} className="font-semibold text-sm bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-red-600 hover:text-white transition-colors">
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        )) : (<tr><td colSpan="5" className="text-center p-12 text-gray-500">No clients found.</td></tr>)}
                        </tbody>
                    </table>
                </div>
                
                {totalClients > pageSize && (
                    <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 rounded-b-xl border-t">
                        <span className="text-sm font-medium text-gray-700">Showing {pageStart}-{pageEnd} of {totalClients}</span>
                        <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Previous</button>
                            <button onClick={() => setCurrentPage(p => p + 1)} disabled={pageEnd >= totalClients || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MagazineClientSection;
